import { Bell, BellRing } from "lucide-react";
import { Dialog, DialogHeader, DialogTrigger, DialogContent } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "@/App";
import { useIsMobile } from "@/hooks/use-mobile";

export type RaidNotification = {
    id: number;
    projectId: number;
    description: string;
    date: Date;
    type?: 0 | 1 | 2 | 3 | 4 | 5 | 6; // e.g., 'issue', 'risk', 'action'
    rating: 0 | 1 | 2; // e.g., 'high', 'medium', 'low'
    seen: boolean; // whether the notification has been seen
}

type NotificationSelectionState = {
    id: string; // e.g., "r1" for risk with id 1
    value: boolean; // whether the notification is selected
}

interface NotificationsProps {
    notifications: RaidNotification[];
    setNotifications: (notifications: RaidNotification[]) => void; // Function to update notifications state
    demoMode?: boolean;
}

const Notifications = ({ notifications, setNotifications, demoMode = false }: NotificationsProps) => {
    const app = React.useContext(AppContext);
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [notificationsSelectionState, setNotificationsSelectionState] = useState<NotificationSelectionState[]>(getInitialSelectedState());

    React.useEffect(() => {
        // get list of unseen notification ids
        const unseenNotifications = notifications.filter(n => !n.seen).map(n => getIdFromTypeAndId(n.id, n.type)); // e.g., "r1" for risk with id 1
        if (unseenNotifications.length > 0) {
            // Report as seen when the component mounts
            reportAsSeen(unseenNotifications);
        }
    }, [notifications]);

    function getInitialSelectedState() {
        const state = notifications.map(n => ({
            id: getIdFromTypeAndId(n.id, n.type), // e.g., "r1" for risk with id 1
            value: false
        }));

        return state;
    }

    function getIdFromTypeAndId(id: number, type?: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
        switch (type) {
            case 0: return "r" + id; // risk
            case 1: return "a" + id; // action
            case 2: return "i" + id; // issue
            case 3: return "d" + id; // decision
            case 4: return "s" + id; // support request
            case 5: return "c" + id; // change request
            case 6: return "n" + id; // charter
            default: return "p" + id; // project
        }
    }

    function reportAsSeen(seen: string[]) {
        if (demoMode) {
            setNotifications(notifications.map(n => {
                    if (seen.includes(getIdFromTypeAndId(n.id, n.type))) {
                        return { ...n, seen: true }; // Mark as seen
                    }
                    return n; // Keep unchanged
                })); // Update notifications state
            return;
        }

        fetch("Reports/NotificationsSeen", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(seen),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error("Failed to mark notifications as seen");
                }
                // update the seen status of notifications to stop showing the bell ring icon
                setNotifications(notifications.map(n => {
                    if (seen.includes(getIdFromTypeAndId(n.id, n.type))) {
                        return { ...n, seen: true }; // Mark as seen
                    }
                    return n; // Keep unchanged
                }
                )); // Update notifications state
            })
            .catch(error => {
                console.error("Error marking notifications as seen:", error);
            });
    }

    function getRating(rating: 0 | 1 | 2, type?: 0 | 1 | 2 | 3 | 4 | 5 | 6) {

        if (!type || type === 1 || type === 3 || type === 6) { // actions, decisions and charter updates do not have ratings
            return (<></>);
        }

        const ratingStr = rating === 2 ? 'high' : rating === 1 ? 'medium' : 'low';

        return (
            <span className={`inline-block px-2 py-1 mt-2 text-xs font-semibold rounded-full ${ratingStr === 'high' ? 'bg-red-100 text-red-800' : ratingStr === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                {ratingStr.charAt(0).toUpperCase() + ratingStr.slice(1)}
            </span>
        );
    }

    function getTypeString(source?: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
        if (source === 0) {
            return "risks";
        }
        if (source === 1) {
            return "actions";
        }
        if (source === 2) {
            return "issues";
        }
        if (source === 3) {
            return "decisions";
        }
        if (source === 4) {
            return "support";
        }
        if (source === 5) {
            return "changeRequest";
        }
        if (source === 6) {
            return "charter";
        }
        return "project"
    }

    function getLocationString(source: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
        if (source === 0) {
            return "/Risks";
        }
        if (source === 1) {
            return "/Actions";
        }
        if (source === 2) {
            return "/Issues";
        }
        if (source === 3) {
            return "/Decisions";
        }
        if (source === 4) {
            return "/SupportRequests";
        }
        if (source === 5) {
            return "/Charter";
        }
        if (source === 6) {
            return "/Charter";
        }
        return "/Charter"
    }

    function getIdColumnName(source: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
        if (source === 0) {
            return "riskId";
        }
        if (source === 1) {
            return "actionId";
        }
        if (source === 2) {
            return "issueId";
        }
        if (source === 3) {
            return "decisionId";
        }
        if (source === 4) {
            return "supportRequestId";
        }
        if (source === 5) {
            return "projectChangeRequestId";
        }
        return "na"
    }

    function handleSelectAll(checked: boolean) {
        setNotificationsSelectionState(prevState => {
            return prevState.map(notification => ({
                ...notification,
                value: checked
            }));
        }
        );
    }

    function handleNotificationSelected(notification: RaidNotification, checked: boolean) {
        const id = getIdFromTypeAndId(notification.id, notification.type);
        setNotificationsSelectionState(prevState => {
            const existingIndex = prevState.findIndex(n => n.id === id);
            if (existingIndex !== -1) {
                // Update existing notification selection state
                const updatedState = [...prevState];
                updatedState[existingIndex] = { ...updatedState[existingIndex], value: checked };
                return updatedState;
            } else {
                // Add new notification selection state
                return [...prevState, { id, value: checked }];
            }
        });
    }

    function getNotificationHeader(type: string) {
        if (type === "changeRequest" || type === "charter") {
            return "bg-table-header text-white font-bold px-2 py-1 rounded";
        }
        return "bg-table-" + type + " text-white font-bold px-2 py-1 rounded";
    }

    function acknowledgeSelectedNotifications() {
        if (notificationsSelectionState.length === 0) {
            return; // No notifications selected
        }

        const acknowledgedIds = notificationsSelectionState.filter(n => n.value).map(n => n.id); // Filter to get only selected notifications
        if (acknowledgedIds.length === 0) {
            console.warn("No notifications selected for acknowledgment");
            return; // No notifications selected for acknowledgment
        }

        if (demoMode) {
            setNotifications(notifications.filter((n: RaidNotification) => !acknowledgedIds.includes(getIdFromTypeAndId(n.id, n.type)))); // Remove acknowledged notifications from the list
            setNotificationsSelectionState(notificationsSelectionState.filter((n: NotificationSelectionState) => !acknowledgedIds.includes(n.id))); // Reset selection state
            return;
        }

        fetch("Reports/NotificationsAcknoledged", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(acknowledgedIds), // Extract the numeric part of the id
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error("Failed to acknowledge notifications");
                }
                // Update the notifications state to remove acknowledged notifications
                setNotifications(notifications.filter((n: RaidNotification) => !acknowledgedIds.includes(getIdFromTypeAndId(n.id, n.type)))); // Remove acknowledged notifications from the list
                setNotificationsSelectionState(notificationsSelectionState.filter((n: NotificationSelectionState) => !acknowledgedIds.includes(n.id))); // Reset selection state
            }
            )
            .catch(error => {
                console.error("Error acknowledging notifications:", error);
            }
            );
    }

    function handleNotificationClicked(notification: RaidNotification) {
        if (app.selectedProject?.id !== notification.projectId) {
            // If the notification is for a different project, set the selected project
            app.setSelectedProject(app.projects.find(project => project.id === notification.projectId)!);
        }

        if (notification.type === 5) {
            // If the notification type is not set, navigate to the project charter
            navigate("/Charter", {
                state: [
                    { id: "status", value: "Open" }
                ]
            });
            return;
        }

        if (notification.type === 6) {
            navigate("/Charter");
            return;
        }

        if (notification.type !== undefined && notification.type !== null) {
            const location = getLocationString(notification.type!);
            const idColumnName = getIdColumnName(notification.type!);
            navigate(location, { state: [{ id: idColumnName, value: notification.id }] });
        }
    }

    return (
        notifications.length > 0 ? (
            <div className="flex flex-col gap-2 max-h-[500px] overflow-y-auto scrollbar p-4">
                <div className={`flex flex-row items-center justify-between w-full bg-button-background text-button-text rounded-lg ${isMobile ? "p-3" : "p-2"}`}>
                    <span className={`font-extrabold ${isMobile ? "text-xl" : "text-lg"}`}>Select All </span>
                    {notifications.length > 0 && notifications.find(n => n.type !== null || n.type !== undefined) && <Checkbox className={isMobile ? "w-6 h-6" : ""} onCheckedChange={(checked) => handleSelectAll(!!checked)} />}
                </div>
                {notifications.map((notification) => (
                    notification.type !== null && notification.type !== undefined ?
                        <div key={notification.id.toString() + notification.type} className={`bg-card shadow rounded-lg ${isMobile ? "p-5" : "p-4"}`} onClick={() => { handleNotificationClicked(notification) }}>
                            <div className={"flex items-center justify-between mb-2 " + getNotificationHeader(getTypeString(notification.type))} >
                                <h3 className={`font-semibold ${isMobile ? "text-xl" : "text-lg"}`}>{getTypeString(notification.type).charAt(0).toUpperCase() + getTypeString(notification.type).slice(1)}</h3>
                                <Checkbox key={(notification.type ? notification.type.toString() : "project") + notification.id}
                                    className={isMobile ? "w-6 h-6" : ""}
                                    onClick={(e) => e.stopPropagation()}
                                    onCheckedChange={(checked) => handleNotificationSelected(notification, !!checked)}
                                    checked={notificationsSelectionState.find(n => n.id === getIdFromTypeAndId(notification.id, notification.type))?.value || false}
                                />
                            </div>
                            <p className={`text-gray-300 ${isMobile ? "text-base" : "text-sm"}`}>{notification.description}</p>
                            <p className={`text-gray-500 ${isMobile ? "text-sm" : "text-xs"}`}>Date: {new Date(notification.date).toLocaleDateString()}</p>
                            {getRating(notification.rating, notification.type)}
                        </div>
                        :
                        <div key={notification.id.toString() + notification.type} className={`bg-card shadow rounded-lg ${isMobile ? "p-5" : "p-4"}`} onClick={() => { handleNotificationClicked(notification) }}>
                            <div className={"flex items-center justify-between mb-2 bg-table-header text-white font-bold px-2 py-1 rounded"} >
                                <h3 className={`font-semibold ${isMobile ? "text-xl" : "text-lg"}`}>Charter Approval Request</h3>
                            </div>
                            <p className={`text-gray-300 ${isMobile ? "text-base" : "text-sm"}`}>{notification.description}</p>
                            <p className={`text-gray-500 ${isMobile ? "text-sm" : "text-xs"}`}>Date: {new Date(notification.date).toLocaleDateString()}</p>
                        </div>
                ))}
                <div>
                    <button className={"button-default w-full " + (isMobile ? "py-6 text-lg" : "")}
                        onClick={() => acknowledgeSelectedNotifications()}
                        disabled={notificationsSelectionState.every(n => !n.value)}
                    >
                        Acknowledge Selected
                    </button>
                </div>
            </div>
        ) : (
            <div className={`bg-white shadow rounded-lg ${isMobile ? "p-5" : "p-4"}`}>
                <p className={`text-gray-600 ${isMobile ? "text-base" : "text-sm"}`}>No notifications available.</p>
            </div>
        )
    )
}

export default Notifications

interface AlertComponentProps {
    notifications: RaidNotification[];
    setNotifications: (notifications: RaidNotification[]) => void; // Optional function to update notifications state
}

export function AlertComponent({ notifications, setNotifications }: AlertComponentProps) {
    const isMobile = useIsMobile();
    return (
        <>
            <Dialog>
                <DialogTrigger>
                    <div id="notificationBell">{notifications.filter(n => !n.seen)?.length > 0 ? <BellRing className={`text-chart-amber animate-wiggle ${isMobile ? "w-7 h-7" : ""}`} /> : <Bell className={notifications.length > 0 ? `text-chart-amber ${isMobile ? "w-7 h-7" : ""}` : ` ${isMobile ? "w-7 h-7" : ""}`} />}</div>
                </DialogTrigger>
                <DialogContent className={isMobile ? "flex flex-col bg-background w-[95vw] max-w-none max-h-[90vh] overflow-y-auto" : "bg-background"}>
                    <DialogHeader>
                        {notifications ? <h2 className={`font-semibold ${isMobile ? "text-2xl" : "text-lg"}`}>Notifications</h2> : <h2 className={`font-semibold ${isMobile ? "text-2xl" : "text-lg"}`}>No Notifications</h2>}
                    </DialogHeader>
                    <Notifications notifications={notifications} setNotifications={setNotifications} />
                </DialogContent>
            </Dialog>
        </>
    );
}